import React, { useContext, useState } from "react";
import { InfluencerContext } from "../../../../context/user/influencerContext";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField
} from "@mui/material";
import { formatFollowers } from "../../../../utils/common/format";
import { EmailOutlined } from "@mui/icons-material";
import './ExternalInfluencersTableStyled.css';
import ExternalInfluencerModal from "../ExternalInfluencerModal/ExternalInfluencerModal";
import { getSocialNetworkIconBySocialNetworkId } from "../../../../utils/constants/socialNetwork";
import { FormattedMessage, useIntl } from "react-intl";

const ExternalInfluencersTable = () => {

    const intl = useIntl();

    const { externalInfluencers } = useContext(InfluencerContext);

    const [open, setOpen] = useState(false);
    const [externalInfluencer, setExternalInfluencer] = useState({});

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(8);

    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('username');

    const [searchTerm, setSearchTerm] = useState(''); // Nuevo estado para la búsqueda

    // Filtrar los influencers según el término de búsqueda
    const filteredInfluencers = externalInfluencers.filter((influencer) => 
        influencer.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        influencer.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sortedInfluencers = [...filteredInfluencers].sort((a, b) => {
        if (orderBy === 'followers' || orderBy === 'engagement') {
            return order === 'desc'
                ? b[orderBy] - a[orderBy]
                : a[orderBy] - b[orderBy];
        } else {
            return order === 'desc'
                ? b[orderBy].localeCompare(a[orderBy])
                : a[orderBy].localeCompare(b[orderBy]);
        }
    });

    const paginatedInfluencers = sortedInfluencers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const getSocialNetworkLink = (influencer) => {
        if (influencer?.social_network === "instagram") {
            return `https://www.instagram.com/${influencer?.username}`;
        } else if (influencer?.social_network === "tiktok") {
            return `https://www.tiktok.com/@${influencer?.username}`;
        } else if (influencer?.social_network === "YouTube") {
            return `https://www.youtube.com/channel/${influencer?.username}`;
        } else {
            return "#";
        }
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    return (
        <>
            <div className="externalInfluencersTable container mt-5 px-auto ">
                <h2 className="fl-text-primary mb-4">
                    <FormattedMessage id="externalInfluencersTableTitle" />
                </h2>

                <TableContainer component={Box}>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                            <TableRow style={{ backgroundColor: "#f4f4f4" }}>
                                <TableCell></TableCell>
                                <TableCell align="left" style={{ fontWeight: 600, maxWidth: 200 }}>
                                    <TableSortLabel
                                        active={orderBy === 'username'}
                                        direction={orderBy === 'username' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'username')}
                                    >
                                        <FormattedMessage id="externalInfluencersHeaderName" />
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center" style={{ fontWeight: 600, minWidth: '15vw' }}>
                                    <TableSortLabel
                                        active={orderBy === 'country'}
                                        direction={orderBy === 'country' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'country')}
                                    >
                                        <FormattedMessage id="externalInfluencersHeaderCountry" />
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center" style={{ fontWeight: 600, minWidth: '15vw' }}>
                                    <TableSortLabel
                                        active={orderBy === 'followers'}
                                        direction={orderBy === 'followers' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'followers')}
                                    >
                                        <FormattedMessage id="externalInfluencersHeaderFollowers" />
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center" style={{ fontWeight: 600, minWidth: '15vw' }}>
                                    <TableSortLabel
                                        active={orderBy === 'engagement'}
                                        direction={orderBy === 'engagement' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'engagement')}
                                    >
                                        <FormattedMessage id="externalInfluencersHeaderEngagement" />
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center" style={{ fontWeight: 600, minWidth: '10vw' }}>
                                    <TextField
                                        label={intl.formatMessage({ id: 'externalInfluencersHeaderSearch' })} // Etiqueta del campo de búsqueda
                                        variant="outlined"
                                        size="small"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ width: '100%', backgroundColor: 'white' }}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedInfluencers.map((item) => (
                                <TableRow
                                    key={item.id}
                                    sx={{ "&:last-child td, &:last-child th": { border: 0, height: '2em' } }}
                                >
                                    <TableCell component="th" scope="row" align="right" style={{ padding: 0 }}>
                                        <a href={getSocialNetworkLink(item)} target="_blank" rel="noreferrer">
                                            <img
                                                src={item.image_profile}
                                                alt={item.username}
                                                style={{
                                                    borderRadius: "50%",
                                                    width: "50px",
                                                    height: "50px",
                                                    marginTop: ".25em",
                                                }}
                                            />
                                        </a>
                                    </TableCell>
                                    <TableCell align="left">
                                        <a href={getSocialNetworkLink(item)} target="_blank" rel="noreferrer" className="text-dark">
                                            <span>{item.username}</span>
                                        </a>
                                    </TableCell>
                                    <TableCell align="center">{item.country}</TableCell>
                                    <TableCell align="center">{formatFollowers(item.followers)}</TableCell>
                                    <TableCell align="center">{item.engagement}%</TableCell>
                                    <TableCell align="center">
                                        <button
                                            className="fl-button-primary button-desktop"
                                            style={{ fontWeight: "bold" }}
                                            onClick={() => {
                                                setOpen(true);
                                                setExternalInfluencer(item);
                                            }}
                                        >
                                            <FormattedMessage id="externalInfluencersCollaborationButton" />
                                        </button>

                                        <button
                                            className="button-mobile d-none fl-button-primary rounded-circle d-flex justify-content-center align-items-center"
                                            style={{ padding: '.375em' }}
                                            onClick={() => {
                                                setOpen(true);
                                                setExternalInfluencer(item);
                                            }}
                                        >
                                            <EmailOutlined color="inherit" style={{ backdropFilter: "white" }} />
                                        </button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={filteredInfluencers.length} // Usar la cantidad de influencers filtrados
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                />
            </div>

            <ExternalInfluencerModal externalInfluencer={externalInfluencer} open={open} setOpen={setOpen} />
        </>
    );
};

export default ExternalInfluencersTable;
